import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, ListChecks, Radio, Video, LogOut, ExternalLink } from "lucide-react";
import { TDIcon, Wordmark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/queue", label: "Editorial Queue", icon: ListChecks },
  { to: "/admin/reviews", label: "Legacy Human Review", icon: ListChecks, adminOnly: true },
  { to: "/admin/sources", label: "Sources", icon: Radio },
  { to: "/admin/videos", label: "Video Queue", icon: Video },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen flex" style={{ background: "#08090A" }}>
      {/* Sidebar */}
      <aside
        className="hidden md:flex flex-col w-64 border-r shrink-0 sticky top-0 h-screen"
        style={{ borderColor: "rgba(255,255,255,0.08)", background: "#0b0c0d" }}
      >
        <div className="px-5 py-5 border-b" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          <div className="flex items-center gap-2.5">
            <TDIcon size={34} />
            <div>
              <Wordmark size="text-base" />
              <div className="td-eyebrow" style={{ color: "#71767c", fontSize: "0.5rem", marginTop: 2 }}>
                Aggregator
              </div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.filter((item) => !item.adminOnly || user?.role === "admin").map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors font-display text-sm ${
                  isActive ? "text-white" : "text-[#A7ABB0] hover:text-white hover:bg-[#111315]"
                }`
              }
              style={({ isActive }) => (isActive ? { background: "rgba(255,138,31,0.1)", boxShadow: "inset 2px 0 0 #FF8A1F" } : {})}
            >
              <Icon size={17} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t space-y-1" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          <a
            href="/"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[#A7ABB0] hover:text-white hover:bg-[#111315] transition-colors font-display text-sm"
          >
            <ExternalLink size={17} /> View public site
          </a>
          <div className="px-3 py-2">
            <div className="text-white text-sm font-display font-medium truncate">{user?.name}</div>
            <div className="td-eyebrow truncate" style={{ color: "#71767c", fontSize: "0.5rem" }}>
              {user?.email}
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[#A7ABB0] hover:text-[#f87171] hover:bg-[#111315] transition-colors font-display text-sm"
          >
            <LogOut size={17} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="md:hidden flex items-center justify-between px-4 py-3 border-b sticky top-0 z-40" style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(8,9,10,0.9)", backdropFilter: "blur(12px)" }}>
          <div className="flex items-center gap-2">
            <TDIcon size={30} />
            <span className="td-eyebrow text-white">Aggregator</span>
          </div>
          <button onClick={onLogout} className="text-[#A7ABB0]"><LogOut size={18} /></button>
        </div>
        <div className="md:hidden flex overflow-x-auto gap-1 px-3 py-2 border-b" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          {NAV.filter((item) => !item.adminOnly || user?.role === "admin").map(({ to, label, end }) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => `td-eyebrow whitespace-nowrap px-3 py-2 rounded ${isActive ? "text-[#FF8A1F] bg-[#111315]" : "text-[#A7ABB0]"}`}>
              {label}
            </NavLink>
          ))}
        </div>

        <main className="flex-1 p-5 md:p-8 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
